import { useLocationCities, useLocationStates } from '@/features/recruiter/hooks/useJobs'

interface JobLocationFieldsProps {
  state: string
  city: string
  onStateChange: (value: string) => void
  onCityChange: (value: string) => void
  stateError?: string
  cityError?: string
}

const LEGACY_STATE_VALUE = '__legacy__'

/**
 * India-only State/UT dropdown plus a city input whose suggestions are the
 * selected state's cities. A city that isn't listed can still be typed —
 * it's saved on the job only, never added to the cities table.
 */
export function JobLocationFields({
  state,
  city,
  onStateChange,
  onCityChange,
  stateError,
  cityError,
}: JobLocationFieldsProps) {
  const { data: states, isPending: isLoadingStates } = useLocationStates()

  const selectedState = states?.find((s) => s.name.toLowerCase() === state.toLowerCase())
  const { data: cities } = useLocationCities(selectedState?.id)

  // An older job may carry a free-text state that isn't in the list.
  const isLegacyState = !!states && state !== '' && !selectedState
  const isUnlistedCity =
    city.trim() !== '' &&
    !!cities &&
    !cities.some((c) => c.name.toLowerCase() === city.trim().toLowerCase())

  const stateGroups = [
    { label: 'States', items: (states ?? []).filter((s) => s.type === 'state') },
    { label: 'Union territories', items: (states ?? []).filter((s) => s.type === 'union_territory') },
  ]

  return (
    <div className="row">
      <div className="col-md-6">
        <div className="mb-3">
          <label htmlFor="location_state" className="form-label">
            State / UT
            <span className="text-danger ms-1">*</span>
          </label>
          <select
            id="location_state"
            className={`form-select ${stateError || isLegacyState ? 'is-invalid' : ''}`.trim()}
            value={isLegacyState ? LEGACY_STATE_VALUE : (selectedState?.name ?? '')}
            onChange={(e) => {
              onStateChange(e.target.value)
              onCityChange('')
            }}
            disabled={isLoadingStates}
            aria-invalid={!!stateError || isLegacyState}
          >
            <option value="" disabled>
              {isLoadingStates ? 'Loading…' : 'Select state'}
            </option>
            {isLegacyState && (
              <option value={LEGACY_STATE_VALUE} disabled>
                {state} (not in list)
              </option>
            )}
            {stateGroups.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.items.map((s) => (
                  <option key={s.id} value={s.name}>
                    {s.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          {stateError ? (
            <div className="invalid-feedback d-block">{stateError}</div>
          ) : isLegacyState ? (
            <div className="invalid-feedback d-block">
              Previously saved as “{state}”. Select a state from the list.
            </div>
          ) : null}
        </div>
      </div>

      <div className="col-md-6">
        <div className="mb-3">
          <label htmlFor="location_city" className="form-label">
            City
          </label>
          <input
            id="location_city"
            type="text"
            list="location_city_options"
            className={`form-control ${cityError ? 'is-invalid' : ''}`.trim()}
            placeholder={selectedState ? 'Search or type a city' : 'Select a state first'}
            value={city}
            onChange={(e) => onCityChange(e.target.value)}
            disabled={!selectedState}
            autoComplete="off"
            maxLength={100}
            aria-invalid={!!cityError}
          />
          <datalist id="location_city_options">
            {(cities ?? []).map((c) => (
              <option key={c.id} value={c.name} />
            ))}
          </datalist>
          {cityError ? (
            <div className="invalid-feedback d-block">{cityError}</div>
          ) : (
            <div className="form-text">
              {!selectedState
                ? 'Unlocks after a state is selected.'
                : isUnlistedCity
                  ? 'Not in our list — it will be saved as typed.'
                  : 'Pick from the list, or type a city that isn’t listed.'}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
